/**
 * 📜 MENTIONS LÉGALES - Heartbeats
 * Version professionnelle et complète
 */

import React from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Heart, 
  Shield, 
  Mail, 
  MapPin, 
  Globe, 
  Building2, 
  Scale, 
  FileText,
  Lock,
  Server,
  Phone,
  Users,
  AlertTriangle,
  Gavel,
  Copyright
} from 'lucide-react';

const SectionCard = ({ icon: Icon, title, color, children, delay = 0 }) => (
  <motion.section 
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    className="bg-white/5 rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-colors"
  >
    <h2 className={`text-xl font-bold ${color} mb-4 flex items-center gap-3`}>
      <div className={`p-2 rounded-lg bg-white/10`}>
        <Icon className="w-5 h-5" />
      </div>
      {title}
    </h2>
    <div className="space-y-3 text-white/80 leading-relaxed">
      {children}
    </div>
  </motion.section>
);

const HeartbeatsMentionsLegales = ({ onBack }) => {
  return (
    <div className="min-h-screen overflow-x-hidden overflow-y-auto bg-gradient-to-b from-[#030014] via-[#0F0529] to-[#030014] text-white" style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#030014]/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button 
            onClick={onBack || (() => window.history.back())}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
            aria-label="Retour"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl">
              <Heart className="w-5 h-5 text-white" fill="currentColor" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Mentions Légales</h1>
              <p className="text-xs text-white/50">heartbeats.fr</p>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="space-y-6">
          
          {/* En-tête */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-10"
          >
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-pink-400 via-purple-400 to-pink-400 bg-clip-text text-transparent mb-2">
              Mentions Légales
            </h1>
            <p className="text-white/60">
              Conformément aux dispositions des articles 6-III et 19 de la Loi n°2004-575 du 21 juin 2004 
              pour la Confiance dans l'Économie Numérique (LCEN)
            </p>
          </motion.div>

          {/* Article 1 - Éditeur du site */}
          <SectionCard 
            icon={Building2} 
            title="Article 1 - Éditeur du site" 
            color="text-pink-400"
            delay={0.1}
          >
            <p className="font-semibold text-lg text-white">HEARTBEATS</p>
            <p>Application de rencontres de proximité et réseau social local</p>
            
            <div className="mt-4 p-4 bg-white/5 rounded-xl space-y-2">
              <p className="flex items-start gap-3">
                <Users className="w-4 h-4 text-pink-400 mt-1 flex-shrink-0" />
                <span><strong>Créateur et Propriétaire :</strong> Monsieur Abdelghani BAALOULOU</span>
              </p>
              <p className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-pink-400 mt-1 flex-shrink-0" />
                <span><strong>Siège social :</strong> 141 Rue Arthur Lamendin, 62980 Vermelles, France</span>
              </p>
              <p className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-pink-400 mt-1 flex-shrink-0" />
                <span><strong>Email :</strong> contact@heartbeats.fr</span>
              </p>
              <p className="flex items-start gap-3">
                <Globe className="w-4 h-4 text-pink-400 mt-1 flex-shrink-0" />
                <span><strong>Site web :</strong> https://heartbeats.fr</span>
              </p>
            </div>
            
            <p className="mt-4 flex items-start gap-3">
              <FileText className="w-4 h-4 text-pink-400 mt-1 flex-shrink-0" />
              <span><strong>Directeur de la publication :</strong> Monsieur Abdelghani BAALOULOU, en qualité de fondateur et représentant légal</span>
            </p>
          </SectionCard>

          {/* Article 2 - Hébergement */}
          <SectionCard 
            icon={Server} 
            title="Article 2 - Hébergement" 
            color="text-purple-400"
            delay={0.15}
          >
            <p>Le site heartbeats.fr est hébergé par :</p>
            
            <div className="mt-3 p-4 bg-white/5 rounded-xl space-y-2">
              <p><strong className="text-white">Emergent Labs, Inc.</strong></p>
              <p>Plateforme d'hébergement cloud sécurisée</p>
              <p className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-purple-400" />
                <span>Site : https://emergent.sh</span>
              </p>
            </div>
            
            <p className="mt-4 text-sm text-white/60">
              L'hébergeur assure la continuité de service et la sécurité des données 
              conformément aux standards de l'industrie et aux réglementations en vigueur.
            </p>
          </SectionCard>

          {/* Article 3 - Propriété intellectuelle */}
          <SectionCard 
            icon={Copyright} 
            title="Article 3 - Propriété intellectuelle" 
            color="text-blue-400"
            delay={0.2}
          >
            <p>
              L'ensemble des éléments constituant le site heartbeats.fr (textes, graphismes, 
              logiciels, photographies, images, vidéos, sons, plans, logos, marques, créations 
              et œuvres protégeables diverses, bases de données, etc.) ainsi que le site lui-même, 
              relèvent des législations françaises et internationales sur le droit d'auteur et 
              la propriété intellectuelle.
            </p>
            
            <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
              <p className="font-semibold text-blue-300 mb-2">Marques déposées</p>
              <p>
                La marque <strong>"Heartbeats"</strong>, le logo associé (cœur stylisé) et l'ensemble 
                de l'identité visuelle sont la propriété exclusive de Monsieur Abdelghani BAALOULOU.
              </p>
            </div>
            
            <p className="mt-4">
              Toute reproduction, représentation, modification, publication, transmission, 
              dénaturation, totale ou partielle du site ou de son contenu, par quelque procédé 
              que ce soit, et sur quelque support que ce soit est interdite sans l'autorisation 
              écrite préalable de Monsieur Abdelghani BAALOULOU.
            </p>
            
            <p className="text-amber-400 font-medium mt-3">
              Toute exploitation non autorisée du site ou de son contenu serait constitutive 
              d'une contrefaçon sanctionnée par les articles L.335-2 et suivants du Code de 
              la Propriété Intellectuelle.
            </p>
          </SectionCard>

          {/* Article 4 - Protection des données personnelles */}
          <SectionCard 
            icon={Lock} 
            title="Article 4 - Protection des données personnelles" 
            color="text-green-400"
            delay={0.25}
          >
            <p>
              Conformément au Règlement (UE) 2016/679 du Parlement européen et du Conseil 
              du 27 avril 2016 relatif à la protection des personnes physiques à l'égard du 
              traitement des données à caractère personnel (RGPD) et à la loi n°78-17 du 
              6 janvier 1978 relative à l'informatique, aux fichiers et aux libertés modifiée, 
              l'utilisateur dispose des droits suivants :
            </p>
            
            <ul className="mt-4 space-y-2 list-none">
              <li className="flex items-start gap-2">
                <span className="text-green-400">✓</span>
                <span>Droit d'accès à ses données personnelles</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400">✓</span>
                <span>Droit de rectification des données inexactes</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400">✓</span>
                <span>Droit à l'effacement (« droit à l'oubli »)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400">✓</span>
                <span>Droit à la limitation du traitement</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400">✓</span>
                <span>Droit à la portabilité des données</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400">✓</span>
                <span>Droit d'opposition au traitement</span>
              </li>
            </ul>
            
            <div className="mt-4 p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
              <p className="font-semibold text-green-300 mb-2">Délégué à la Protection des Données (DPO)</p>
              <p>Pour exercer vos droits ou pour toute question relative à la protection de vos données :</p>
              <p className="mt-2 flex items-center gap-2">
                <Mail className="w-4 h-4 text-green-400" />
                <strong>privacy@heartbeats.fr</strong>
              </p>
            </div>
            
            <p className="mt-4 text-sm text-white/60">
              Vous disposez également du droit d'introduire une réclamation auprès de la 
              Commission Nationale de l'Informatique et des Libertés (CNIL) : www.cnil.fr
            </p>
          </SectionCard>

          {/* Article 5 - Limitation de responsabilité */}
          <SectionCard 
            icon={AlertTriangle} 
            title="Article 5 - Limitation de responsabilité" 
            color="text-amber-400"
            delay={0.3}
          >
            <p>
              Heartbeats s'efforce d'assurer au mieux de ses possibilités l'exactitude et la 
              mise à jour des informations diffusées sur ce site. Toutefois, Heartbeats ne 
              peut garantir l'exactitude, la précision ou l'exhaustivité des informations 
              mises à disposition sur ce site.
            </p>
            
            <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
              <p className="font-semibold text-amber-300 mb-2">Responsabilité relative aux rencontres</p>
              <p>
                Heartbeats agit en tant qu'intermédiaire technique de mise en relation. 
                L'éditeur décline expressément toute responsabilité quant aux rencontres 
                effectuées par le biais de la plateforme. Les utilisateurs sont seuls 
                responsables de leurs interactions, échanges et rendez-vous.
              </p>
            </div>
            
            <p className="mt-4">
              <strong className="text-white">Recommandations de sécurité :</strong> Les utilisateurs 
              sont vivement invités à faire preuve de prudence lors de leurs rencontres et à 
              privilégier systématiquement les lieux publics pour les premiers rendez-vous.
            </p>
          </SectionCard>

          {/* Article 6 - Cookies et traceurs */}
          <SectionCard 
            icon={Shield} 
            title="Article 6 - Cookies et traceurs" 
            color="text-cyan-400"
            delay={0.35}
          >
            <p>
              Le site heartbeats.fr utilise des cookies et autres traceurs dans le respect 
              de la réglementation en vigueur, notamment la directive 2002/58/CE « vie privée 
              et communications électroniques » et les lignes directrices de la CNIL.
            </p>
            
            <p className="mt-3">
              Ces cookies permettent d'assurer le bon fonctionnement du site, d'améliorer 
              l'expérience utilisateur et de réaliser des statistiques de fréquentation.
            </p>
            
            <p className="mt-3">
              L'utilisateur peut à tout moment gérer ses préférences en matière de cookies 
              via les paramètres de son navigateur ou via notre bandeau de gestion des cookies.
            </p>
          </SectionCard>

          {/* Article 7 - Liens hypertextes */}
          <SectionCard 
            icon={Globe} 
            title="Article 7 - Liens hypertextes" 
            color="text-indigo-400"
            delay={0.4}
          >
            <p>
              Le site heartbeats.fr peut contenir des liens hypertextes vers d'autres sites 
              web. Ces liens sont fournis à titre informatif et ne sauraient engager la 
              responsabilité de l'éditeur quant au contenu de ces sites externes.
            </p>
            
            <p className="mt-3">
              La création de liens hypertextes vers le site heartbeats.fr est soumise à 
              l'autorisation préalable et écrite de l'éditeur.
            </p>
          </SectionCard>

          {/* Article 8 - Droit applicable et juridiction */}
          <SectionCard 
            icon={Gavel} 
            title="Article 8 - Droit applicable et juridiction compétente" 
            color="text-red-400"
            delay={0.45}
          >
            <p>
              Les présentes mentions légales sont régies par le droit français.
            </p>
            
            <p className="mt-3">
              En cas de litige relatif à l'interprétation ou l'exécution des présentes, 
              et à défaut d'accord amiable, compétence expresse est attribuée aux tribunaux 
              compétents du ressort de la Cour d'Appel de Douai, nonobstant pluralité de 
              défendeurs ou appel en garantie.
            </p>
            
            <div className="mt-4 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
              <p className="text-sm">
                Conformément à l'article L.612-1 du Code de la consommation, tout consommateur 
                a le droit de recourir gratuitement à un médiateur de la consommation en vue 
                de la résolution amiable du litige qui l'oppose à un professionnel.
              </p>
            </div>
          </SectionCard>

          {/* Article 9 - Contact */}
          <SectionCard 
            icon={Mail} 
            title="Article 9 - Contact" 
            color="text-pink-400"
            delay={0.5}
          >
            <p>
              Pour toute question ou demande d'information concernant le site, ou tout 
              signalement de contenus ou d'activités illicites, l'utilisateur peut 
              contacter l'éditeur :
            </p>
            
            <div className="mt-4 p-4 bg-gradient-to-r from-pink-500/10 to-purple-500/10 border border-pink-500/30 rounded-xl space-y-3">
              <p className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-pink-400" />
                <span><strong>Email général :</strong> contact@heartbeats.fr</span>
              </p>
              <p className="flex items-center gap-3">
                <Lock className="w-5 h-5 text-green-400" />
                <span><strong>Protection des données :</strong> privacy@heartbeats.fr</span>
              </p>
              <p className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-purple-400" />
                <span><strong>Adresse :</strong> 141 Rue Arthur Lamendin, 62980 Vermelles, France</span>
              </p>
            </div>
          </SectionCard>

          {/* Footer */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-center pt-8 pb-4 border-t border-white/10"
          >
            <p className="text-white/40 text-sm">
              Dernière mise à jour : Février 2026
            </p>
            <p className="text-white/30 text-xs mt-2">
              © 2026 Heartbeats - Tous droits réservés - Abdelghani BAALOULOU
            </p>
          </motion.div>

        </div>
      </main>
    </div>
  );
};

export default HeartbeatsMentionsLegales;
